import { useState } from "react";
import { api } from "@/lib/api";
import { bi } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { MessageSquare, Send, UserRound, Sparkles, CircleDot } from "lucide-react";

const UI = {
  tr: { title: "Kategori Yorumları", desc: "Öğrencinin her kategoriye yazdığı cevapları güçlendirmek için yorum bırakın. Yorumlar öğrencinin ekranında anında görünür.", yourName: "Adınız (danışman)", namePh: "Örn. Ayşe Yılmaz", general: "Genel Değerlendirme", empty: "Boş", records: "kayıt", ph: "Bu kategori için öneriniz...", send: "Yorum Gönder", nameReq: "Lütfen adınızı girin", sent: "Yorum gönderildi", none: "Henüz yorum yok", note: "Danışman notu", ai: "AI Önerisi", aiBusy: "Öneri hazırlanıyor...", aiHint: "AI önerisi metin kutusuna yazılır; düzenleyip gönderebilirsiniz.", limit: "Çok fazla istek; bir dakika bekleyin." },
  en: { title: "Category Comments", desc: "Leave comments to strengthen the student's answers in each category. Comments appear instantly on the student's screen.", yourName: "Your name (advisor)", namePh: "e.g. Jane Smith", general: "Overall Assessment", empty: "Empty", records: "records", ph: "Your suggestion for this category...", send: "Send Comment", nameReq: "Please enter your name", sent: "Comment sent", none: "No comments yet", note: "Advisor note", ai: "AI Suggestion", aiBusy: "Preparing suggestion...", aiHint: "The AI suggestion is written into the box; edit and send it.", limit: "Too many requests; wait a minute." },
};

function CommentList({ items, lang, u }) {
  if (!items.length) return <div className="text-xs italic text-[#94a3b8]">{u.none}</div>;
  return (
    <ul className="space-y-2">
      {items.map((c) => (
        <li key={c.id} data-testid={`comment-${c.id}`} className="text-[13px] bg-white border border-[#e2e8f0] rounded-lg px-3 py-2">
          <div className="flex justify-between text-[11px] text-[#64748b] mb-0.5"><span className="font-semibold text-[#1a2e4a]">{c.author}</span><span>{String(c.created_at).slice(0, 10)}</span></div>
          <div className="text-[#334155] whitespace-pre-wrap">{c.text}</div>
        </li>
      ))}
    </ul>
  );
}

export const AdvisorCommentPanel = ({ token, cv, config, lang, onAdded, author, setAuthor }) => {
  const u = UI[lang];
  const [drafts, setDrafts] = useState({});
  const [sending, setSending] = useState(null);
  const [suggesting, setSuggesting] = useState(null);

  const suggest = async (key) => {
    setSuggesting(key);
    try {
      const { data } = await api.post(`/shared/${token}/suggest`, { categoryKey: key, language: lang });
      setDrafts((d) => ({ ...d, [key]: data.comment }));
    } catch (e) { toast.error(e.response?.status === 429 ? u.limit : (e.response?.data?.detail || "AI error")); }
    finally { setSuggesting(null); }
  };
  const comments = cv.comments || {};
  const cats = [{ key: "general", label: { tr: u.general, en: u.general } }, ...config.categories];

  const send = async (key) => {
    const text = (drafts[key] || "").trim();
    if (!author.trim()) return toast.error(u.nameReq);
    if (!text) return;
    setSending(key);
    try {
      localStorage.setItem("dsa_advisor_name", author.trim());
      const { data } = await api.post(`/shared/${token}/comments`, { categoryKey: key, author: author.trim(), text });
      onAdded(data, key);
      setDrafts((d) => ({ ...d, [key]: "" }));
      toast.success(u.sent);
    } catch { toast.error("Error"); }
    finally { setSending(null); }
  };

  return (
    <aside data-testid="advisor-comment-panel" className="bg-white rounded-xl border border-[#e2e8f0] p-5 lg:sticky lg:top-20">
      <div className="flex items-center gap-2 mb-1"><MessageSquare className="h-5 w-5 text-[#c0392b]" /><h2 className="font-display font-bold text-xl text-[#1a2e4a]">{u.title}</h2></div>
      <p className="text-xs text-[#64748b] mb-4">{u.desc}</p>
      <Label className="text-[13px] text-[#1a2e4a] flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" /> {u.yourName}</Label>
      <Input data-testid="advisor-name-input" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder={u.namePh} className="mt-1 mb-4" />
      <Accordion type="single" collapsible className="max-h-[60vh] overflow-y-auto thin-scroll pr-1">
        {cats.map((c) => {
          const list = comments[c.key] || [];
          const count = c.key === "general" ? null : (cv.categories?.[c.key] || []).length;
          const note = c.key === "general" ? "" : bi(cv.notes?.[c.key], lang);
          return (
            <AccordionItem key={c.key} value={c.key}>
              <AccordionTrigger data-testid={`comment-cat-${c.key}`} className="py-2.5 text-sm text-[#1a2e4a] hover:no-underline">
                <span className="flex items-center gap-2 flex-1 text-left">
                  <span className="truncate">{c.label[lang]}</span>
                  {count === 0 && <span className="text-[10px] rounded-full bg-[#fadbd8] text-[#c0392b] px-1.5 py-0.5 font-semibold">{u.empty}</span>}
                  {count > 0 && <span className="text-[10px] text-[#94a3b8]">{count} {u.records}</span>}
                  {list.length > 0 && <span className="ml-auto mr-2 text-[10px] rounded-full bg-[#1a2e4a] text-white px-1.5 py-0.5">{list.length}</span>}
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                {note && <div className="text-xs text-[#64748b] bg-[#f8fafc] border border-dashed border-[#cbd5e1] rounded-md px-2.5 py-1.5"><span className="font-semibold">{u.note}:</span> {note}</div>}
                <CommentList items={list} lang={lang} u={u} />
                <Textarea data-testid={`comment-input-${c.key}`} value={drafts[c.key] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [c.key]: e.target.value }))} rows={3} placeholder={u.ph} className="bg-white" />
                <div className="flex flex-wrap items-center gap-2">
                  <Button data-testid={`comment-send-${c.key}`} size="sm" disabled={sending === c.key} onClick={() => send(c.key)} className="bg-[#c0392b] hover:bg-[#a93226] text-white"><Send className="h-3.5 w-3.5 mr-1.5" /> {u.send}</Button>
                  <Button data-testid={`comment-ai-${c.key}`} size="sm" variant="outline" disabled={suggesting === c.key} onClick={() => suggest(c.key)} title={u.aiHint} className="border-[#1a2e4a] text-[#1a2e4a]">
                    {suggesting === c.key ? <CircleDot className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />} {suggesting === c.key ? u.aiBusy : u.ai}
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </aside>
  );
};
